import { api } from './api';
import {
  CreateInboxRequest,
  Inbox,
  InboxFormBinding,
  InboxFormBindingItem,
  UpdateInboxMembersRequest,
  UpdateInboxRequest,
} from '../types/inbox';
import {
  MetaWabaSubscription,
  MetaWebhookHealthStatus,
  MetaWebhookProvisioningStatus,
} from '../types/metaWebhook';

class InboxService {
  async getAll(): Promise<Inbox[]> {
    const response = await api.get<Inbox[]>('/inboxes');
    return response.data;
  }

  async getById(id: string): Promise<Inbox> {
    const response = await api.get<Inbox>(`/inboxes/${id}`);
    return response.data;
  }

  async create(data: CreateInboxRequest): Promise<Inbox> {
    const response = await api.post<Inbox>('/inboxes', data);
    return response.data;
  }

  async update(id: string, data: UpdateInboxRequest): Promise<Inbox> {
    const response = await api.put<Inbox>(`/inboxes/${id}`, data);
    return response.data;
  }

  async delete(id: string): Promise<void> {
    await api.delete(`/inboxes/${id}`);
  }

  async updateMembers(id: string, data: UpdateInboxMembersRequest): Promise<Inbox> {
    const response = await api.put<Inbox>(`/inboxes/${id}/members`, data);
    return response.data;
  }

  async getForms(id: string): Promise<InboxFormBinding[]> {
    const response = await api.get<InboxFormBinding[]>(`/inboxes/${id}/forms`);
    return response.data;
  }

  async replaceForms(id: string, forms: InboxFormBindingItem[]): Promise<InboxFormBinding[]> {
    const response = await api.put<InboxFormBinding[]>(`/inboxes/${id}/forms`, { forms });
    return response.data;
  }

  async removeBotAssignment(id: string): Promise<Inbox> {
    const response = await api.delete<Inbox>(`/inboxes/${id}/bot`);
    return response.data;
  }

  async uploadWidgetAvatar(id: string, file: File): Promise<{ hasAvatar: boolean; avatarUrl?: string | null }> {
    const formData = new FormData();
    formData.append('image', file);
    const response = await api.put<{ hasAvatar: boolean; avatarUrl?: string | null }>(
      `/inboxes/${id}/widget-avatar`,
      formData
    );
    return response.data;
  }

  async deleteWidgetAvatar(id: string): Promise<{ hasAvatar: boolean; avatarUrl?: string | null }> {
    const response = await api.delete<{ hasAvatar: boolean; avatarUrl?: string | null }>(
      `/inboxes/${id}/widget-avatar`
    );
    return response.data;
  }

  async registerMetaWebhook(id: string): Promise<MetaWebhookProvisioningStatus> {
    const response = await api.post<MetaWebhookProvisioningStatus>(`/inboxes/${id}/meta-webhook/register`);
    return response.data;
  }

  async getMetaWebhookHealth(id: string): Promise<MetaWebhookHealthStatus> {
    const response = await api.get<MetaWebhookHealthStatus>(`/inboxes/${id}/meta-webhook/health`);
    return response.data;
  }

  async getMetaWebhookSubscriptions(id: string): Promise<MetaWabaSubscription[]> {
    const response = await api.get<MetaWabaSubscription[]>(`/inboxes/${id}/meta-webhook/subscriptions`);
    return response.data;
  }
}

export const inboxService = new InboxService();
